import { type ReactNode } from 'react';
import { Outlet } from '@tanstack/react-router';
import { Box, AppBar, Toolbar, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { Sidebar } from './Sidebar';
import { PageHeaderProvider } from '../../contexts/PageHeaderContext';
import { HeaderActions } from './HeaderActions';
import { sxColFill, sxColFillScroll } from '../../theme/sxMixins';
import { useSidebarState } from '../../features/menu/hooks/useSidebarState';
import { useUserProfile } from '../../features/user/hooks/useUserProfile';
import { PageSubHeader } from './PageSubHeader';

const SIDEBAR_WIDTH = 280;
const SIDEBAR_WIDTH_MD = 240;

export const MainLayout = ({ children }: { children?: ReactNode } = {}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMedium = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { sidebarOpen, handleSidebarToggle } = useSidebarState(isMobile);
  const { userProfile, loading: profileLoading } = useUserProfile();

  const sidebarWidth = isMedium ? SIDEBAR_WIDTH_MD : SIDEBAR_WIDTH;

  return (
    <PageHeaderProvider>
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          position: 'fixed',
          top: 0,
          left: 0,
        }}
      >
        <AppBar
          position="fixed"
          sx={{ zIndex: theme.zIndex.drawer + 1, backgroundColor: 'primary.main' }}
        >
          <Toolbar sx={{ gap: 1 }}>
            <IconButton
              color="inherit"
              aria-label="toggle sidebar"
              edge="start"
              onClick={() => handleSidebarToggle(!sidebarOpen)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ flex: 1 }} />

            <HeaderActions userProfile={userProfile} profileLoading={profileLoading} />
          </Toolbar>
        </AppBar>

        <Sidebar
          width={sidebarWidth}
          open={sidebarOpen}
          onClose={() => handleSidebarToggle(false)}
          variant={isMobile ? 'temporary' : 'persistent'}
        />

        <Box
          component="main"
          sx={{
            ...sxColFillScroll,
            flex: 1,
            pt: 1.5,
            pb: 1.5,
            px: 0,
            maxWidth: '100%',
            height: '100%',
          }}
        >
          <Toolbar />
          <PageSubHeader />
          <Box sx={sxColFill}>{children ?? <Outlet />}</Box>
        </Box>
      </Box>
    </PageHeaderProvider>
  );
};

export default MainLayout;
